import {
  FormControl,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import {
  DropdownChoice,
  DropdownItem,
} from '@shared/models/common.model';
import { MemoAttachment } from '@shared/models/memo.model';
import {
  getRefVariableMap,
  getRefVariables,
  isCyclicReference,
} from '@shared/utils/formula.function';
import { allValuesNullOrUndefined } from '@shared/utils/utils';
import { Parser } from 'expr-eval';
import { cloneDeep } from 'lodash';
import { customAlphabet, nanoid } from 'nanoid';
import { evaluateExpression } from '../../memos/components/upload-memos/form-inputs/input-formula/formula.utils';
import {
  EFORM_EXP_PARSER_OPTIONS,
  getReferableColumns,
  parseColumnFormula,
} from './eform.function';
import { TranslateService } from '@ngx-translate/core';

export enum EFormTypes {
  row = 'row',
  empty = 'empty',
  heading = 'heading',
  text = 'text',
  richtext = 'richtext',
  email = 'email',
  date = 'date',
  image = 'image',
  phone = 'phone',
  checkbox = 'checkbox',
  radio = 'radio',
  dropdown = 'dropdown',
  file = 'file',
  location = 'location',
  take_a_photo = 'take_a_photo',
  signature = 'signature',
  table = 'table',
}

export enum EFormFileTypes {
  document = 'document',
  spreadsheet = 'spreadsheet',
  pdf = 'pdf',
  presentation = 'presentation',
  image = 'image',
}

export enum EFormTableColumnTypes {
  text = 'text',
  textarea = 'textarea',
  checkbox = 'checkbox',
  radio = 'radio',
  dropdown = 'dropdown',
  email = 'email',
  date = 'date',
  number = 'number',
  phone = 'phone',
  currency = 'currency',
  image = 'image',
  formula = 'formula',
}

export type EFormTableCalculableColumnTypes =
  | EFormTableColumnTypes.number
  | EFormTableColumnTypes.formula;

interface SiteCodeDropdownContext {
  display: string;
  display_en: string;
  code: string;
}

export type SiteCodeDropdownItem = DropdownItem<
  number,
  SiteCodeDropdownContext
>;

export function generateFileKey(): string {
  return customAlphabet('1234567890abcdef', 10)();
}

export class EForm {
  id?: number;
  template: EFormTemplate;
  attachments: EFormAttachment[];

  constructor(eForm?: Partial<EForm>, applyAttachments = true) {
    this.id = eForm?.id;
    this.template = new EFormTemplate(eForm?.template);

    // applyAttachments() must always be after new EFormTemplate() call
    // because there is the use of "instance of" condition in applyAttachments()
    applyAttachments
      ? this.applyAttachments(eForm?.attachments ?? [])
      : (this.attachments = eForm?.attachments ?? []);
  }

  applyAttachments(attachments: EFormAttachment[]): void {
    const attachmentsMap = new Map(
      attachments
        .filter((attachment) => 'file_key' in attachment)
        .map((attachment) => [
          (attachment as EFormAttachment).file_key,
          attachment,
        ]),
    );

    const updateFileElements = (
      element:
        | EFormFileElement
        | EFormImageElement
        | EFormTakePhotoElement,
    ): void => {
      if (element instanceof EFormFileElement) {
        for (const file of element.files) {
          const attachment = attachmentsMap.get(file.file_key);
          if (attachment) {
            file.file = attachment.memo_attachment.file;
            file.file_size = attachment.memo_attachment.file_size;
          }
        }
        return;
      }

      if (
        element instanceof EFormImageElement ||
        element instanceof EFormTakePhotoElement
      ) {
        const attachment = attachmentsMap.get(element.file_key);
        if (attachment) {
          element.file = attachment.memo_attachment.file;
          element.previewFile = attachment.memo_attachment.file;
        }
      }
    };

    // Update file elements with corresponding attachment data
    this.template.rows = this.template.rows.map((row) => ({
      ...row,
      children: row.children.map((element) => {
        if (
          element instanceof EFormImageElement ||
          element instanceof EFormFileElement ||
          element instanceof EFormTakePhotoElement
        ) {
          updateFileElements(element);
        }
        return element;
      }),
    }));

    // assign
    this.attachments = attachments;
  }

  getAllFileData(newOnly = true): EFormFileData[] {
    // filter all file elements
    let fileElements: (
      | EFormFileElement
      | EFormImageElement
      | EFormTakePhotoElement
    )[] = this.template.rows.flatMap(
      (row) =>
        row.children.filter(
          (element) =>
            element instanceof EFormImageElement ||
            element instanceof EFormFileElement ||
            element instanceof EFormTakePhotoElement,
        ) as (
          | EFormFileElement
          | EFormImageElement
          | EFormTakePhotoElement
        )[],
    );

    // filter all newly uploaded file elements
    if (newOnly) {
      fileElements = fileElements
        .filter((fileElement) => {
          if (fileElement instanceof EFormFileElement) {
            return fileElement.files.some(
              (file) => file.file && typeof file.file !== 'string',
            );
          }

          return (
            fileElement.file && typeof fileElement.file !== 'string'
          );
        })
        .map((fileElement) => {
          if (fileElement instanceof EFormFileElement) {
            // need to copy by value
            // to prevent filter affecting actual element in the payload
            const clonedFileElement = new EFormFileElement(
              fileElement,
            );

            clonedFileElement.files = fileElement.files.filter(
              (file) => typeof file.file !== 'string',
            );

            return clonedFileElement;
          }
          return fileElement;
        });
    }

    // helper function to transform element to fileData
    const getFileData = (
      element:
        | EFormFileElement
        | EFormImageElement
        | EFormTakePhotoElement,
    ): EFormFileData[] => {
      if (element instanceof EFormFileElement) {
        return element.files.map((file) => ({
          file: file.file,
          type: EFormTypes.file,
          file_key: file.file_key,
        }));
      }

      return [
        {
          file: element.file,
          type: EFormTypes.image,
          file_key: element.file_key,
        },
      ];
    };

    return fileElements
      .map((fileElement) => getFileData(fileElement))
      .flat();
  }

  validate(mode: 'admin' | 'user' = 'user'): boolean {
    return this.template.rows
      .flatMap((row) =>
        row.children.map((element) => element.validate(mode)),
      )
      .every((isValid) => isValid);
  }

  areAllElementsValid(): boolean {
    return this.template.rows.every((row) =>
      row.children.every((element) => element.hasNoErrors()),
    );
  }

  checkDDocSignatureWidget(translateService: TranslateService) {
    // check that section 26/28 must have only 1 widget in EForm.
    const widgetExists = new Set<string>();
    const errorMsg: string[] = [];

    for (let i = 0; i < this.template.rows.length; i++) {
      const row = this.template.rows[i];
      if (!row || !row.children) continue;
      for (let j = 0; j < row.children.length; j++) {
        const item = row.children[j];
        if (!item || item.type !== 'signature') continue;
        const signatureWidgetValue = (item as EFormSignatureElement)
          ?.value;
        if (!signatureWidgetValue?.ddoc_enable) continue;

        const key = `${signatureWidgetValue?.level},${signatureWidgetValue?.member_seq}`;
        if (widgetExists.has(key)) {
          const msg = translateService
            .instant('MEMOS.EFORM.SIG-WIDGET-DDOC-MORE-THAN-1')
            .replace('<<loa_level>>', signatureWidgetValue?.level);

          if (!errorMsg.includes(msg)) {
            errorMsg.push(msg);
          }
        }
        widgetExists.add(key);
      }
    }

    return errorMsg;
  }

  getDto(): EForm {
    return {
      ...this,
      template: {
        ...this.template,
        rows: this.template.rows.map((row) => ({
          ...row,
          children: row.children.map((element) => element.getDto()),
        })),
      },
    };
  }
}

export class EFormTemplate {
  rows: EFormTemplateRow[];

  constructor(eFormTempl?: Partial<EFormTemplate>) {
    if (!eFormTempl?.rows?.length) {
      this.rows = [new EFormTemplateRow()];
      return;
    }

    this.rows = eFormTempl.rows.map(
      (row) => new EFormTemplateRow(row),
    );
  }
}

export class EFormTemplateRow {
  type = EFormTypes.row;
  children: EFormElement[];

  constructor(EFormTemplateRow?: Partial<EFormTemplateRow>) {
    if (!EFormTemplateRow?.children?.length) {
      this.children = [new EFormEmptyElement()];
      return;
    }

    this.children = EFormTemplateRow.children.map((element) => {
      return createElementByType(element.type, element);
    });
  }
}

export function createElementByType(
  type: EFormTypes,
  element?: EFormElement,
): EFormElement {
  const cloneElement = cloneDeep(element);
  switch (type) {
    case EFormTypes.heading:
      return new EFormHeadingElement(
        cloneElement as EFormHeadingElement,
      );
    case EFormTypes.text:
      return new EFormTextElement(cloneElement as EFormTextElement);
    case EFormTypes.richtext:
      return new EFormRichtextElement(
        cloneElement as EFormRichtextElement,
      );
    case EFormTypes.email:
      return new EFormEmailElement(cloneElement as EFormEmailElement);
    case EFormTypes.date:
      return new EFormDateElement(cloneElement as EFormDateElement);
    case EFormTypes.image:
      return new EFormImageElement(cloneElement as EFormImageElement);
    case EFormTypes.phone:
      return new EFormPhoneElement(cloneElement as EFormPhoneElement);
    case EFormTypes.checkbox:
      return new EFormCheckboxElement(
        cloneElement as EFormCheckboxElement,
      );
    case EFormTypes.radio:
      return new EFormRadioElement(cloneElement as EFormRadioElement);
    case EFormTypes.dropdown:
      return new EFormDropdownElement(
        cloneElement as EFormDropdownElement,
      );
    case EFormTypes.file:
      return new EFormFileElement(cloneElement as EFormFileElement);
    case EFormTypes.location:
      return new EFormLocationElement(
        cloneElement as EFormLocationElement,
      );
    case EFormTypes.take_a_photo:
      return new EFormTakePhotoElement(
        cloneElement as EFormTakePhotoElement,
      );
    case EFormTypes.signature:
      return new EFormSignatureElement(
        cloneElement as EFormSignatureElement,
      );
    case EFormTypes.table:
      return new EFormTableElement(cloneElement as EFormTableElement);
    default:
      return new EFormEmptyElement();
  }
}

export class EFormElementBase {
  errors: ValidationErrors = {};

  validate(mode: 'admin' | 'user' = 'user'): boolean {
    this.errors = {};

    if (mode === 'admin') {
      this.validateForAdmin();
    }

    if (mode === 'user') {
      this.validateForUser();
    }

    return this.hasNoErrors();
  }

  protected validateForAdmin(): void {}

  protected validateForUser(): void {}

  protected isValidEmail(email: string): boolean {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  getDto<T>(): T {
    return {} as T;
  }

  hasNoErrors(): boolean {
    return Object.keys(this.errors).length === 0;
  }
}

export class EFormEmptyElement extends EFormElementBase {
  type = EFormTypes.empty;

  override getDto<EFormEmptyElement>(): EFormEmptyElement {
    return { type: this.type } as EFormEmptyElement;
  }
}

export class EFormHeadingElement extends EFormElementBase {
  type = EFormTypes.heading;

  name: string; // title
  value: string; // description

  size: 'small' | 'medium' | 'large';

  // no need for api
  label = 'Heading';
  icon =
    'assets/images/icons/eform/element-icons/heading.svg#heading';
  fullWidth = true;

  constructor(element?: Partial<EFormHeadingElement>) {
    super();

    this.name = element?.name ?? '';
    this.value = element?.value ?? '';

    this.size = element?.size ?? 'medium';
  }

  override validateForAdmin(): void {
    if (!this.name?.trim().length) {
      this.errors['name'] = 'Heading is required';
    }
  }

  override getDto<EFormHeadingElement>(): EFormHeadingElement {
    return {
      type: this.type,
      name: this.name,
      value: this.value,
      size: this.size,
    } as EFormHeadingElement;
  }
}

export class EFormTextElement extends EFormElementBase {
  type = EFormTypes.text;

  name: string;
  value: string;

  showTitle: boolean;
  require: boolean;
  placeholder: string;
  min: number;
  max: number;

  // no need for api
  label = 'Text';
  icon = 'assets/images/icons/eform/element-icons/text.svg#text';

  constructor(element?: Partial<EFormTextElement>) {
    super();

    this.name = element?.name ?? '';
    this.value = element?.value ?? '';

    this.showTitle = element?.showTitle ?? true;
    this.require = element?.require ?? false;
    this.placeholder =
      element?.placeholder ?? 'Type your response here';
    this.min = element?.min ?? 0;
    this.max = element?.max ?? 0;
  }

  override validateForAdmin(): void {
    if (this.showTitle && !this.name?.trim().length) {
      this.errors['name'] = 'Title is required';
    }
    if (this.value?.length) {
      this.valueMinMaxValidation();
    }
  }

  override validateForUser(): void {
    if (this.require && !this.value?.trim().length) {
      this.errors['value'] = 'Value is required';
    }
    this.valueMinMaxValidation();
  }

  private valueMinMaxValidation(): void {
    if (this.min && this.value.trim().length < this.min) {
      this.errors['value'] = 'Value must be greater than ' + this.min;
    }
    if (this.max && this.value.trim().length > this.max) {
      this.errors['value'] = 'Value must be less than ' + this.max;
    }
    if (
      this.min &&
      this.max &&
      (this.value.trim().length < this.min ||
        this.value.trim().length > this.max)
    ) {
      this.errors['value'] =
        `Value must be between ${this.min} and ${this.max}`;
    }
  }

  override getDto<EFormTextElement>(): EFormTextElement {
    return {
      type: this.type,
      name: this.name,
      value: this.value,
      showTitle: this.showTitle,
      require: this.require,
      placeholder: this.placeholder,
      min: this.min,
      max: this.max,
    } as EFormTextElement;
  }
}

export class EFormRichtextElement extends EFormElementBase {
  type = EFormTypes.richtext;

  name: string;
  value: string;

  showTitle: boolean;

  // no need for api
  label = 'Richtext';
  icon =
    'assets/images/icons/eform/element-icons/richtext.svg#richtext';
  fullWidth = true;

  constructor(element?: Partial<EFormRichtextElement>) {
    super();

    this.name = element?.name ?? '';
    this.value = element?.value ?? '';

    this.showTitle = element?.showTitle ?? true;
  }

  override validateForAdmin(): void {
    if (this.showTitle && !this.name?.trim().length) {
      this.errors['name'] = 'Title is required';
    }
  }

  override getDto<EFormRichtextElement>(): EFormRichtextElement {
    return {
      type: this.type,
      name: this.name,
      value: this.value,
      showTitle: this.showTitle,
    } as EFormRichtextElement;
  }
}

export class EFormEmailElement extends EFormElementBase {
  type = EFormTypes.email;

  name: string;
  value: string;

  showTitle: boolean;
  require: boolean;
  placeholder: string;

  // no need for api
  label = 'Email';
  icon = 'assets/images/icons/eform/element-icons/email.svg#email';

  constructor(element?: Partial<EFormEmailElement>) {
    super();

    this.name = element?.name ?? '';
    this.value = element?.value ?? '';

    this.showTitle = element?.showTitle ?? true;
    this.require = element?.require ?? false;
    this.placeholder =
      element?.placeholder ?? 'Type your placeholder here';
  }

  override validateForAdmin(): void {
    if (this.showTitle && !this.name?.trim().length) {
      this.errors['name'] = 'Title is required';
    }
    if (this.value?.length && !this.isValidEmail(this.value)) {
      this.errors['value'] = 'Enter a valid email';
    }
  }

  override validateForUser(): void {
    if (this.require && !this.value?.trim().length) {
      this.errors['value'] = 'Value is required';
    }
    if (this.value?.length && !this.isValidEmail(this.value)) {
      this.errors['value'] = 'Enter a valid email';
    }
  }

  override getDto<EFormEmailElement>(): EFormEmailElement {
    return {
      type: this.type,
      name: this.name,
      value: this.value,
      showTitle: this.showTitle,
      require: this.require,
      placeholder: this.placeholder,
    } as EFormEmailElement;
  }
}

export class EFormDateElement extends EFormElementBase {
  type = EFormTypes.date;

  name: string;
  value: string | null;

  showTitle: boolean;
  require: boolean;
  format: string;
  placeholder: string;

  // no need for api
  label = 'Date';
  icon = 'assets/images/icons/eform/element-icons/date.svg#date';

  constructor(element?: Partial<EFormDateElement>) {
    super();

    this.name = element?.name ?? '';
    this.value = element?.value ?? null;

    this.showTitle = element?.showTitle ?? true;
    this.require = element?.require ?? false;
    this.format = element?.format ?? 'EN:%-d/%-m/%Y';
    this.placeholder =
      element?.placeholder ?? 'Type your placeholder here';
  }

  override validateForAdmin(): void {
    if (this.showTitle && !this.name?.trim().length) {
      this.errors['name'] = 'Title is required';
    }
  }

  override validateForUser(): void {
    if (this.require && !this.value?.trim().length) {
      this.errors['value'] = 'Value is required';
    }
  }

  override getDto<EFormDateElement>(): EFormDateElement {
    return {
      type: this.type,
      name: this.name,
      value: this.value,
      showTitle: this.showTitle,
      require: this.require,
      format: this.format,
      placeholder: this.placeholder,
    } as EFormDateElement;
  }
}

export class EFormImageElement extends EFormElementBase {
  type = EFormTypes.image;

  name: string;
  file_key: string;
  file_name: string;

  showTitle: boolean;
  require: boolean;
  scale: number;
  align: 'left' | 'middle' | 'right';

  // no need for api
  file: File | string | null;
  previewFile: ArrayBuffer | string | null;
  label = 'Image';
  icon = 'assets/images/icons/eform/element-icons/image.svg#image';
  fullWidth = true;

  constructor(
    element?: Partial<EFormImageElement>,
    createNewKey = false,
  ) {
    super();

    this.name = element?.name ?? '';
    this.file_name = element?.file_name ?? '';

    const generatedKey = generateFileKey();
    this.file_key = createNewKey
      ? generatedKey
      : (element?.file_key ?? generatedKey);

    this.showTitle = element?.showTitle ?? true;
    this.require = element?.require ?? false;
    this.scale = element?.scale ?? 100;
    this.align = element?.align ?? 'middle';

    this.file = element?.file ?? null;
    this.previewFile = element?.previewFile ?? null;
  }

  override validateForAdmin(): void {
    if (this.showTitle && !this.name?.trim().length) {
      this.errors['name'] = 'Title is required';
    }
  }

  override validateForUser(): void {
    if (this.require && !this.file) {
      this.errors['file'] = 'This field is required';
    }
  }

  override getDto<EFormImageElement>(): EFormImageElement {
    return {
      type: this.type,
      name: this.name,
      file_key: this.file_key,
      file_name: this.file_name,
      showTitle: this.showTitle,
      require: this.require,
      scale: this.scale,
      align: this.align,
    } as EFormImageElement;
  }
}

export class EFormPhoneElement extends EFormElementBase {
  type = EFormTypes.phone;

  name: string;
  value: string;

  showTitle: boolean;
  require: boolean;
  placeholder: string;

  // no need for api
  label = 'Phone';
  icon = 'assets/images/icons/eform/element-icons/phone.svg#phone';

  constructor(element?: Partial<EFormPhoneElement>) {
    super();

    this.name = element?.name ?? '';
    this.value = element?.value ?? '';

    this.showTitle = element?.showTitle ?? true;
    this.require = element?.require ?? false;
    this.placeholder =
      element?.placeholder ?? 'Type your placeholder here';
  }

  override validateForAdmin(): void {
    if (this.showTitle && !this.name?.trim().length) {
      this.errors['name'] = 'Title is required';
    }
  }

  override validateForUser(): void {
    if (this.require && !this.value?.trim().length) {
      this.errors['value'] = 'Value is required';
    }
  }

  override getDto<EFormPhoneElement>(): EFormPhoneElement {
    return {
      type: this.type,
      name: this.name,
      value: this.value,
      showTitle: this.showTitle,
      require: this.require,
      placeholder: this.placeholder,
    } as EFormPhoneElement;
  }
}

export class EFormCheckboxElement extends EFormElementBase {
  type = EFormTypes.checkbox;

  name: string;
  value: EFormElementOption[];

  showTitle: boolean;
  require: boolean;

  // no need for api
  label = 'Checkbox';
  icon =
    'assets/images/icons/eform/element-icons/checkbox.svg#checkbox';

  constructor(element?: Partial<EFormCheckboxElement>) {
    super();

    this.name = element?.name ?? '';
    this.value = element?.value ?? [
      {
        text: '',
        selected: true,
      },
    ];

    this.showTitle = element?.showTitle ?? true;
    this.require = element?.require ?? false;
  }

  override validateForAdmin(): void {
    if (this.showTitle && !this.name?.trim().length) {
      this.errors['name'] = 'Title is required';
    }
    if (
      this.value &&
      !this.value.some((option) => option.text?.trim().length)
    ) {
      this.errors['value'] = 'Some option names are empty';
    }
    if (
      this.value &&
      this.value.map((option) => option.text).length >
        new Set(this.value.map((option) => option.text)).size
    ) {
      this.errors['value'] = 'Some option names are duplicated';
    }
  }

  override validateForUser(): void {
    if (
      this.require &&
      !this.value?.some((option) => option.selected)
    ) {
      this.errors['value'] = 'Value is required';
    }
  }

  override getDto<EFormCheckboxElement>(): EFormCheckboxElement {
    return {
      type: this.type,
      name: this.name,
      value: this.value,
      showTitle: this.showTitle,
      require: this.require,
    } as EFormCheckboxElement;
  }
}

export class EFormRadioElement extends EFormElementBase {
  type = EFormTypes.radio;

  name: string;
  value: EFormElementOption[];

  showTitle: boolean;
  require: boolean;

  // no need for api
  label = 'Radio Button';
  icon = 'assets/images/icons/eform/element-icons/radio.svg#radio';

  constructor(element?: Partial<EFormCheckboxElement>) {
    super();

    this.name = element?.name ?? '';
    this.value = element?.value ?? [
      {
        text: '',
        selected: true,
      },
    ];

    this.showTitle = element?.showTitle ?? true;
    this.require = element?.require ?? false;
  }

  override validateForAdmin(): void {
    if (this.showTitle && !this.name?.trim().length) {
      this.errors['name'] = 'Title is required';
    }
    if (
      this.value &&
      !this.value.some((option) => option.text?.trim().length)
    ) {
      this.errors['value'] = 'Some option names are empty';
    }
    if (
      this.value &&
      this.value.map((option) => option.text).length >
        new Set(this.value.map((option) => option.text)).size
    ) {
      this.errors['value'] = 'Some option names are duplicated';
    }
  }

  override validateForUser(): void {
    if (
      this.require &&
      !this.value?.filter((option) => option.selected).length
    ) {
      this.errors['value'] = 'Value is required';
    }
  }

  override getDto<EFormRadioElement>(): EFormRadioElement {
    return {
      type: this.type,
      name: this.name,
      value: this.value,
      showTitle: this.showTitle,
      require: this.require,
    } as EFormRadioElement;
  }
}

export class EFormDropdownElement extends EFormElementBase {
  type = EFormTypes.dropdown;

  name: string;
  value: EFormElementOption[];

  showTitle: boolean;
  require: boolean;
  placeholder: string;
  multi: boolean;

  // no need for api
  label = 'Dropdown';
  icon =
    'assets/images/icons/eform/element-icons/dropdown.svg#dropdown';

  constructor(element?: Partial<EFormDropdownElement>) {
    super();

    this.name = element?.name ?? '';
    this.value = element?.value ?? [
      {
        text: '',
        selected: false,
      },
    ];

    this.showTitle = element?.showTitle ?? true;
    this.require = element?.require ?? false;
    this.placeholder = element?.placeholder ?? 'No selected';
    this.multi = element?.multi ?? false;
  }

  override validateForAdmin(): void {
    if (this.showTitle && !this.name?.trim().length) {
      this.errors['name'] = 'Title is required';
    }
    if (
      this.value &&
      !this.value.some((option) => option.text?.trim().length)
    ) {
      this.errors['value'] = 'Some option names are empty';
    }
    if (
      this.value &&
      this.value.map((option) => option.text).length >
        new Set(this.value.map((option) => option.text)).size
    ) {
      this.errors['value'] = 'Some option names are duplicated';
    }
  }

  override validateForUser(): void {
    if (
      this.require &&
      !this.value?.filter((option) => option.selected).length
    ) {
      this.errors['value'] = 'Value is required';
    }
  }

  override getDto<EFormDropdownElement>(): EFormDropdownElement {
    return {
      type: this.type,
      name: this.name,
      value: this.value,
      showTitle: this.showTitle,
      require: this.require,
      multi: this.multi,
      placeholder: this.placeholder,
    } as EFormDropdownElement;
  }
}

export class EFormFileElement extends EFormElementBase {
  type = EFormTypes.file;

  name: string;
  description: string;

  showTitle: boolean;
  showDescription: boolean;
  require: boolean;
  is_specific_type: boolean;
  accept_types: EFormFileTypes[];
  max_file_count: number;
  files: EFormFile[];

  // no need for api
  label = 'File Upload';
  icon = 'assets/images/icons/eform/element-icons/file.svg#file';
  fullWidth = true;

  constructor(element?: Partial<EFormFileElement>) {
    super();

    this.name = element?.name ?? '';
    this.description = element?.description ?? '';

    this.showTitle = element?.showTitle ?? true;
    this.showDescription = element?.showDescription ?? true;
    this.require = element?.require ?? false;
    this.is_specific_type = element?.is_specific_type ?? false;
    this.accept_types = element?.accept_types ?? [];
    this.max_file_count = element?.max_file_count ?? 1;
    this.files = element?.files ?? [];
  }

  protected override validateForAdmin(): void {
    if (this.showTitle && !this.name?.trim().length) {
      this.errors['name'] = 'Title is required';
    }

    if (
      this.max_file_count &&
      this.files.length > this.max_file_count
    ) {
      this.errors['value'] =
        'Can only upload up to ' + this.max_file_count + ' file(s)';
    }

    if (this.is_specific_type && !this.accept_types.length) {
      this.errors['value'] =
        'Must choose at least one accept type in the settings';
    }
  }

  protected override validateForUser(): void {
    if (this.require && !this.files.length) {
      this.errors['value'] = 'This field is required';
    }

    if (
      this.max_file_count &&
      this.files.length > this.max_file_count
    ) {
      this.errors['value'] =
        'Can only upload up to ' + this.max_file_count + ' files';
    }
  }

  override getDto<EFormFileElement>(): EFormFileElement {
    return {
      type: this.type,
      name: this.name,
      description: this.description,
      showTitle: this.showTitle,
      showDescription: this.showDescription,
      require: this.require,
      is_specific_type: this.is_specific_type,
      accept_types: this.accept_types,
      max_file_count: this.max_file_count,
      files: this.files.map((file) => ({
        file_name: file.file_name,
        file_key: file.file_key,
      })),
    } as EFormFileElement;
  }
}

interface EFormLocationValue {
  latitude: number;
  longitude: number;
}

export class EFormLocationElement extends EFormElementBase {
  type = EFormTypes.location;

  name: string;
  value?: EFormLocationValue;

  buttonName: string;
  require: boolean;
  showTitle: boolean;

  // no need for api
  label = 'Location';
  icon =
    'assets/images/icons/eform/element-icons/location.svg#location';

  constructor(element?: Partial<EFormLocationElement>) {
    super();

    this.name = element?.name ?? '';
    this.value = element?.value;

    this.buttonName = element?.buttonName ?? 'Grant location access';
    this.require = element?.require ?? false;
    this.showTitle = element?.showTitle ?? true;
  }

  override validateForAdmin(): void {
    if (this.showTitle && !this.name?.trim().length) {
      this.errors['name'] = 'Title is required';
    }
  }

  override validateForUser(): void {
    if (this.require && !this.value) {
      this.errors['value'] = 'Location is required';
    }
  }

  override getDto<EFormLocationElement>(): EFormLocationElement {
    return {
      type: this.type,
      name: this.name,
      value: this.value,
      buttonName: this.buttonName,
      require: this.require,
      showTitle: this.showTitle,
    } as EFormLocationElement;
  }
}

export class EFormTakePhotoElement extends EFormElementBase {
  type = EFormTypes.take_a_photo;

  name: string;
  file_key: string;
  file_name: string;

  showTitle: boolean;
  require: boolean;

  // no need for api
  file: File | string | null;
  previewFile: ArrayBuffer | string | null;

  // no need for api
  label = 'Take Photo';
  icon =
    'assets/images/icons/eform/element-icons/take_photo.svg#take_photo';
  fullWidth = true;

  constructor(element?: Partial<EFormTakePhotoElement>) {
    super();

    this.name = element?.name ?? '';
    this.file_key = element?.file_key ?? '';
    this.file_name = element?.file_name ?? '';

    this.showTitle = element?.showTitle ?? true;
    this.require = element?.require ?? false;

    this.file = element?.file ?? null;
    this.previewFile = element?.previewFile ?? null;
  }

  override validateForAdmin(): void {
    if (this.showTitle && !this.name?.trim().length) {
      this.errors['name'] = 'Title is required';
    }
  }

  override validateForUser(): void {
    if (this.require && !this.file) {
      this.errors['value'] = 'Photo is required';
    }
  }

  override getDto<EFormTakePhotoElement>(): EFormTakePhotoElement {
    return {
      type: this.type,
      name: this.name,
      file_key: this.file_key,
      file_name: this.file_name,
      showTitle: this.showTitle,
      require: this.require,
    } as EFormTakePhotoElement;
  }
}

export interface EFormSignatureValue {
  level: number;
  member_seq: number;
  full_name?: string;
  position?: string;
  ddoc_enable: boolean;

  // no need for api
  signature?: string;
}

export class EFormSignatureElement extends EFormElementBase {
  type = EFormTypes.signature;

  value?: EFormSignatureValue;

  showTitle: boolean;
  showTitleApproverType: boolean;
  approveByLabel: string;
  showJobPosition: boolean;
  showApproveDate: boolean;
  dateFormat?: string;
  dateLabel: string;

  label = 'Signature';
  icon =
    'assets/images/icons/eform/element-icons/signature.svg#signature';

  constructor(element?: Partial<EFormSignatureElement>) {
    super();

    this.value = element?.value;

    this.showTitleApproverType =
      element?.showTitleApproverType ?? true;
    this.showApproveDate = element?.showApproveDate ?? true;
    this.showJobPosition = element?.showJobPosition ?? true;
    this.showTitle = element?.showTitle ?? true;
    this.approveByLabel = element?.approveByLabel ?? 'Approve by';
    this.dateLabel = element?.dateLabel ?? 'Date';
    this.dateFormat = element?.dateFormat;
  }

  override validateForAdmin(): void {
    if (!this.value) {
      this.errors['value'] = 'Signature is required';
    } else if (typeof this.value === 'object') {
      if (allValuesNullOrUndefined(this.value)) {
        this.errors['value'] = 'Signature is required';
      }
    }
  }

  override getDto<EFormSignatureElement>(): EFormSignatureElement {
    return {
      type: this.type,
      value: {
        level: this.value?.level,
        member_seq: this.value?.member_seq,
        full_name: this.value?.full_name,
        position: this.value?.position,
        ddoc_enable: this.value?.ddoc_enable,
      },
      showTitleApproverType: this.showTitleApproverType,
      showApproveDate: this.showApproveDate,
      showJobPosition: this.showJobPosition,
      showTitle: this.showTitle,
      approveByLabel: this.approveByLabel,
      dateFormat: this.dateFormat,
      dateLabel: this.dateLabel,
    } as EFormSignatureElement;
  }
}

export class EFormTableElement extends EFormElementBase {
  type = EFormTypes.table;

  name: string;
  value: EFormTableRow[];

  showTitle: boolean;
  can_add_row: boolean;
  columns: EFormTableColumn[];

  // no need for api
  label = 'Table';
  icon = 'assets/images/icons/eform/element-icons/table.svg#table';
  fullWidth = true;
  totalRow?: Record<string, any>;

  constructor(element?: Partial<EFormTableElement>) {
    super();

    this.name = element?.name ?? '';
    this.value = element?.value ?? [];

    this.showTitle = element?.showTitle ?? true;
    this.can_add_row = element?.can_add_row ?? false;
    this.columns = element?.columns ?? [
      new EFormTableColumn(),
      new EFormTableColumn(),
    ];
  }

  override validateForAdmin(): void {
    if (this.showTitle && !this.name?.trim().length) {
      this.errors['name'] = 'Title is required';
    }

    if (!(this.columns && this.columns.length)) {
      this.errors['value'] = 'Table has no columns';
      return;
    }

    const hasEmptyColumnName = this.columns.some(
      (column) => !column.name?.trim().length,
    );
    if (hasEmptyColumnName) {
      this.errors['value'] = 'Some column headers are empty';
      return;
    }

    const invalidChoiceValIndexes: number[] = [];
    const requiredChoiceValIndexes: number[] = [];
    const dupChoicesIndexes: number[] = [];
    const requiredFormulaIndexes: number[] = [];
    const nonExistingIndexes: number[] = [];
    const invalidFormulaIndexes: number[] = [];
    const decimalRegex = /^[+-]?\d*\.?\d+$/;
    this.columns.forEach((column, colIndex) => {
      if (column.columnType === EFormTableColumnTypes.formula) {
        if (!column.expression) {
          requiredFormulaIndexes.push(colIndex + 1);
          return;
        }
        // Check variable existence
        try {
          parseColumnFormula(
            column.expression,
            'key',
            'name',
            getReferableColumns(this.columns),
          );
        } catch (error) {
          nonExistingIndexes.push(colIndex);
          return;
        }
        // Check formula correctness
        try {
          const variables = getRefVariables(column.expression);
          const varValMap = variables.reduce(
            (obj, varKey) => {
              obj[varKey] = 1;
              return obj;
            },
            {} as Record<string, number>,
          );
          evaluateExpression(
            new Parser(EFORM_EXP_PARSER_OPTIONS),
            column.expression,
            varValMap,
          );
        } catch (error) {
          invalidFormulaIndexes.push(colIndex);
          return;
        }
      } else if (
        column.columnType === EFormTableColumnTypes.radio ||
        column.columnType === EFormTableColumnTypes.dropdown
      ) {
        const isDuplicateChoice =
          column.choices.length > new Set(column.choices).size;
        if (isDuplicateChoice) {
          dupChoicesIndexes.push(colIndex + 1);
        }
        column.choices.forEach((choice) => {
          if (column.isForCalculate) {
            const value = (choice as DropdownChoice<string | null>)
              .value;
            const invalid = value == null || value === '';
            if (invalid) {
              requiredChoiceValIndexes.push(colIndex + 1);
            } else if (!decimalRegex.test(value)) {
              invalidChoiceValIndexes.push(colIndex + 1);
            }
          }
        });
      }
    });
    // * Choice column validation
    if (dupChoicesIndexes.length) {
      this.errors['value'] = 'Some columns have duplicate choices';
      return;
    }
    if (requiredChoiceValIndexes.length) {
      const columnAt = requiredChoiceValIndexes.join(', ');
      this.errors['value'] =
        `Some choices have no value in columns ${columnAt}.`;
      return;
    }
    if (invalidChoiceValIndexes.length) {
      const columnAt = invalidChoiceValIndexes.join(', ');
      this.errors['value'] =
        `Some choices are not numeric in columns ${columnAt}.`;
      return;
    }
    // * Formula column validation
    if (requiredFormulaIndexes.length) {
      const columnAt = requiredFormulaIndexes.join(', ');
      this.errors['value'] =
        `No formula is provided for columns ${columnAt}`;
      return;
    }
    if (nonExistingIndexes.length) {
      const columnAt = nonExistingIndexes.join(', ');
      this.errors['value'] =
        `Some variable names for formula columns ${columnAt} were not found.`;
      return;
    }
    if (invalidFormulaIndexes.length) {
      const columnAt = invalidFormulaIndexes.join(', ');
      this.errors['value'] = `Invalid formula in columns ${columnAt}`;
      return;
    }
    // validate recursive reference
    const refMap = getRefVariableMap(
      this.columns,
      'key',
      'expression',
    );
    const isCyclic = isCyclicReference(refMap);
    if (isCyclic) {
      this.errors['value'] =
        'Some formulas contain recursive references';
      return;
    }

    if (!this.can_add_row && !(this.value && this.value.length)) {
      this.errors['value'] = 'Table has no rows';
      return;
    }

    const hasInvalidEmail = this.value.some((row) =>
      Object.keys(row).some((key) => {
        const isEmailColumn = this.columns.some(
          (column) =>
            column.key === key &&
            column.columnType === EFormTableColumnTypes.email,
        );
        return (
          isEmailColumn &&
          new FormControl(row[key], [Validators.email]).invalid
        );
      }),
    );
    if (hasInvalidEmail) {
      this.errors['value'] = 'Table has an invalid email';
      return;
    }
  }

  protected override validateForUser(): void {
    const hasInvalidEmail = this.value?.some((row) =>
      Object.keys(row).some((key) => {
        const isEmailColumn = this.columns.some(
          (column) =>
            column.key === key &&
            column.columnType === EFormTableColumnTypes.email,
        );
        return (
          isEmailColumn &&
          new FormControl(row[key], [Validators.email]).invalid
        );
      }),
    );
    if (hasInvalidEmail) {
      this.errors['value'] = 'Table has an invalid email';
      return;
    }

    const hasEmptyRequired = this.value?.some((row) =>
      Object.keys(row).some((key) => {
        const isRequiredColumn = this.columns.some(
          (column) => column.key === key && column.require,
        );
        return (
          isRequiredColumn && !(row[key] as string)?.trim().length
        );
      }),
    );
    if (hasEmptyRequired) {
      this.errors['value'] = 'Table has an empty required field';
      return;
    }
  }

  override getDto<EFormTableElement>(): EFormTableElement {
    return {
      type: this.type,
      name: this.name,
      value: this.value,
      showTitle: this.showTitle,
      can_add_row: this.can_add_row,
      columns: this.columns,
      totalRow: this.totalRow,
    } as EFormTableElement;
  }
}

export type EFormElement =
  | EFormEmptyElement
  | EFormHeadingElement
  | EFormTextElement
  | EFormRichtextElement
  | EFormEmailElement
  | EFormDateElement
  | EFormPhoneElement
  | EFormCheckboxElement
  | EFormImageElement
  | EFormFileElement
  | EFormLocationElement
  | EFormTakePhotoElement
  | EFormSignatureElement;

export interface EFormElementOption {
  text: string;
  selected: boolean;
}

export interface EFormFileData {
  file_key?: string;
  file: string | File | null;
  type: 'file' | 'image';
}

export interface EFormAttachment {
  id: number;
  eform: number;
  file_key: string;
  is_image: boolean;
  memo_attachment: MemoAttachment;
}

export interface EFormFile {
  file_name: string;
  file_key: string;
  file: string | File | null;
  file_size: number;
}

export class EFormTableColumn {
  type = 'table_column';
  key: string;
  name: string;
  columnType: EFormTableColumnTypes;
  expression?: string | null;
  isForCalculate?: boolean;
  require: boolean;
  placeholder: string | null;
  choices: (string | DropdownItem<string | null>)[];
  system_column_name: string;
  showTotalRow?: boolean;

  constructor(eFormTableColumn?: Partial<EFormTableColumn>) {
    this.key = eFormTableColumn?.key ?? nanoid();
    this.name = eFormTableColumn?.name ?? '';
    this.columnType =
      eFormTableColumn?.columnType ?? EFormTableColumnTypes.text;
    this.require = eFormTableColumn?.require ?? false;
    this.placeholder = eFormTableColumn?.placeholder ?? null;
    this.choices = eFormTableColumn?.choices ?? [];
  }
}

export class EFormTableRow {
  [key: string]: string | string[] | null;
}
